import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  alpha,
  Box,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "../../i18n";
import { fetchData } from "api/apiHelpers";
import VideoGlassContainer from "./VideoGlassContainer";
import { athenaColors } from "../landing/colors";

interface PatientStatus {
  patient_index: number;
  patient_name: string;
  status: string;
  created_patient_id: number | null;
  error_message: string;
}

interface JobData {
  job_id: string;
  status: string;
  total_patients: number;
  total_documents: number;
  processed_patients: number;
  processed_documents: number;
  progress_percent: number;
  created_patient_ids: number[];
  errors: string[];
  started_at: string | null;
  completed_at: string | null;
  patient_statuses: PatientStatus[];
}

function statusChip(
  status: string,
  t: (key: string, def: string) => string,
) {
  switch (status) {
    case "completed":
      return (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
          label={t("bulk_results.status_completed", "Completed")}
          size="small"
          color="success"
          variant="outlined"
          sx={{ fontWeight: 600, fontSize: "0.72rem" }}
        />
      );
    case "failed":
    case "error":
      return (
        <Chip
          icon={<ErrorIcon sx={{ fontSize: 14 }} />}
          label={t("bulk_results.status_failed", "Failed")}
          size="small"
          color="error"
          variant="outlined"
          sx={{ fontWeight: 600, fontSize: "0.72rem" }}
        />
      );
    default:
      return (
        <Chip
          icon={<HourglassTopIcon sx={{ fontSize: 14 }} />}
          label={status || "Processing"}
          size="small"
          color="warning"
          variant="outlined"
          sx={{ fontWeight: 600, fontSize: "0.72rem" }}
        />
      );
  }
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

export default function BulkUploadResults() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { jobId } = useParams<{ jobId: string }>();

  const [job, setJob] = useState<JobData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current?.closest("[data-glass-container]") as HTMLElement | null;
    if (!el) return;
    el.dataset.emptyDashboard = "true";
    return () => { delete el.dataset.emptyDashboard; };
  }, []);

  const fetchJob = useCallback(async () => {
    if (!jobId) return;
    try {
      const data = await fetchData<JobData>(
        `/patients/bulk-upload/status/${jobId}/`,
      );
      setJob(data);
      setError(null);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("bulk_results.load_error", "Failed to load job details."),
      );
    } finally {
      setLoading(false);
    }
  }, [jobId, t]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  const succeededCount =
    job?.patient_statuses.filter((p) => p.status === "completed").length ?? 0;
  const failedCount =
    job?.patient_statuses.filter(
      (p) => p.status === "failed" || p.status === "error",
    ).length ?? 0;

  const handleExportCsv = useCallback(() => {
    if (!job) return;
    const rows = [["#", "Patient", "Status", "Patient ID", "Error"]];
    for (const p of job.patient_statuses) {
      rows.push([
        String(p.patient_index + 1),
        p.patient_name,
        p.status,
        p.created_patient_id ? String(p.created_patient_id) : "",
        p.error_message || "",
      ]);
    }
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bulk_upload_${jobId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [job, jobId]);

  return (
    <Container
      ref={containerRef}
      disableGutters
      maxWidth={false}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
        p: { md: 0.5, lg: 2,  xl: 3 },
        minHeight: 0,
      }}
    >
      <VideoGlassContainer variant="dark">
        <Box sx={{ minHeight: 300 }}>
          {/* Header: back arrow + title + summary chips */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2.5,
            }}
          >
            <IconButton
              onClick={() => navigate("/dashboard")}
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: "50%",
                background: alpha(athenaColors.deepNavy, 0.4),
                color: athenaColors.white,
                backdropFilter: "blur(6px)",
                border: `1px solid ${alpha(athenaColors.periwinkle, 0.4)}`,
                boxShadow: theme.custom?.glassShadow,
                "&:hover": {
                  background: alpha(athenaColors.deepNavy, 0.6),
                  borderColor: athenaColors.periwinkle,
                },
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>

            <CloudUploadIcon
              sx={{ fontSize: 22, color: alpha(athenaColors.white, 0.6) }}
            />
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: athenaColors.white, flex: 1 }}
            >
              {t("bulk_results.title", "Bulk Upload Results")}
            </Typography>

            {job && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Chip
                  label={`${succeededCount} ${t("bulk_results.succeeded", "succeeded")}`}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: "0.72rem" }}
                />
                {failedCount > 0 && (
                  <Chip
                    label={`${failedCount} ${t("bulk_results.failed", "failed")}`}
                    size="small"
                    color="error"
                    variant="outlined"
                    sx={{ fontWeight: 600, fontSize: "0.72rem" }}
                  />
                )}
                <Tooltip title={t("bulk_results.export_csv", "Export as CSV")}>
                  <IconButton size="small" onClick={handleExportCsv}>
                    <DownloadIcon
                      sx={{ fontSize: 18, color: alpha(athenaColors.white, 0.6) }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          {/* Summary bar */}
          {job && (
            <Box
              sx={{
                display: "flex",
                gap: 3,
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                background: alpha(athenaColors.deepNavy, 0.35),
                border: `1px solid ${alpha(athenaColors.white, 0.1)}`,
                flexWrap: "wrap",
              }}
            >
              {[
                [t("bulk_results.patients_label", "Patients"), job.total_patients],
                [t("bulk_results.documents_label", "Documents"), job.total_documents],
                [t("bulk_results.status_label", "Status"), job.status.replace(/_/g, " ")],
                [t("bulk_results.started_label", "Started"), formatDateTime(job.started_at)],
                [t("bulk_results.completed_label", "Completed"), formatDateTime(job.completed_at)],
              ].map(([label, value]) => (
                <Box key={String(label)}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: alpha(athenaColors.white, 0.5),
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                      fontWeight: 600,
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: athenaColors.white,
                      fontWeight: 600,
                      textTransform: "capitalize",
                    }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Loading / error states */}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={36} sx={{ color: athenaColors.periwinkle }} />
            </Box>
          )}

          {error && (
            <Typography
              sx={{ py: 4, textAlign: "center", color: athenaColors.white }}
            >
              {error}
            </Typography>
          )}

          {/* Patient table */}
          {job && !loading && (
            <TableContainer
              sx={{
                maxHeight: 520,
                borderRadius: 2,
                border: `1px solid ${alpha(athenaColors.white, 0.12)}`,
                background: alpha(athenaColors.deepNavy, 0.2),
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {["#", "Patient", "Status", ""].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          fontWeight: 700,
                          color: athenaColors.white,
                          fontSize: "0.78rem",
                          background: alpha(athenaColors.deepNavy, 0.85),
                          backdropFilter: "blur(8px)",
                          borderBottom: `1px solid ${alpha(athenaColors.white, 0.15)}`,
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {job.patient_statuses
                    .sort((a, b) => a.patient_index - b.patient_index)
                    .map((p) => (
                      <TableRow
                        key={p.patient_index}
                        hover
                        sx={{
                          cursor: p.created_patient_id ? "pointer" : "default",
                          "&:hover": {
                            background: alpha(athenaColors.periwinkle, 0.08),
                          },
                        }}
                        onClick={() => {
                          if (p.created_patient_id)
                            navigate(
                              `/patients/${p.created_patient_id}/details`,
                            );
                        }}
                      >
                        <TableCell
                          sx={{
                            color: alpha(athenaColors.white, 0.6),
                            fontSize: "0.75rem",
                            width: 50,
                            borderBottom: `1px solid ${alpha(athenaColors.white, 0.06)}`,
                          }}
                        >
                          {p.patient_index + 1}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: athenaColors.white,
                            fontWeight: 600,
                            fontSize: "0.82rem",
                            borderBottom: `1px solid ${alpha(athenaColors.white, 0.06)}`,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.8,
                            }}
                          >
                            {p.patient_name.replace(/_/g, " ")}
                            {p.error_message && (
                              <Tooltip title={p.error_message}>
                                <ErrorIcon
                                  sx={{
                                    fontSize: 14,
                                    color: "error.main",
                                    cursor: "help",
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: `1px solid ${alpha(athenaColors.white, 0.06)}`,
                          }}
                        >
                          {statusChip(p.status, t)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            width: 50,
                            borderBottom: `1px solid ${alpha(athenaColors.white, 0.06)}`,
                          }}
                        >
                          {p.created_patient_id && (
                            <Link
                              href={`/patients/${p.created_patient_id}/details`}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                e.preventDefault();
                                navigate(
                                  `/patients/${p.created_patient_id}/details`,
                                );
                              }}
                              sx={{ color: athenaColors.periwinkle }}
                            >
                              <OpenInNewIcon sx={{ fontSize: 16 }} />
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </VideoGlassContainer>
    </Container>
  );
}
